import { put, takeEvery } from "redux-saga/effects";
import UserEvents from "./UserEvents";
import UserApi from "./UserApi";
import {
    AccessableEmail,
    AccessableEmailDomain,
    User,
    UserData,
    UserList,
    UserSkill,
    UserLicense,
} from "./User";
import {
    UpdateUserProfileImage,
    UpdateUserProfileImageComplete,
    GetUserInfoListComplete,
    UpdateUserInfo,
    UpdateUserInfoComplete,
    UpdateUserInfoFailed,
    LoginStarted,
    LoginComplete,
    LoginFailed,
    GetSkillList,
    GetSkillListComplete,
    StartSyncBatchComplete,
    StartSyncBatchFailed,
    GetGroupListComplete,
    UpdateGroupList,
    UpdateGroupListComplete,
    UpdateAccessableEmailDomainListComplete,
    UpdateAccessableEmailListComplete,
    GetAccessableEmailDomainListComplete,
    GetAccessableEmailListComplete,
    UpdateAccessableEmailDomainList,
    UpdateAccessableEmailList,
    GetUserLicenseStart,
    GetUserLicenseComplete,
    CreateNewSkill,
    CreateNewSkillCompleted,
    CreateNewSkillFailed,
} from "./UserActions";

function* Login({ user }: ReturnType<typeof LoginStarted>) {
    const userInfo: UserData = yield UserApi.GetUser(user);
    if (userInfo && userInfo.token && userInfo.refreshToken) {
        yield put(LoginComplete(userInfo));
        localStorage.setItem("cineonhub_access_token", userInfo.token);
        localStorage.setItem("cineonhub_access_refreshToken", userInfo.refreshToken);
    } else {
        yield put(LoginFailed(userInfo));
    }
}

// ユーザー一覧取得（管理画面用）
function* GetUserInfoList() {
    const listItems: UserList[] = yield UserApi.GetUserInfoList();
    yield put(GetUserInfoListComplete(listItems));
}

// ユーザー一覧更新（管理画面用）
function* UpdateUserInfoWorker({ updateListItems }: ReturnType<typeof UpdateUserInfo>) {
    const listItems: any = yield UserApi.UpdateUserInfo(updateListItems);
    if (listItems.response && listItems.response.status !== 200) {
        yield put(UpdateUserInfoFailed());
    } else {
        yield put(UpdateUserInfoComplete(listItems));
    }
}

function* UpdateProfileImage({
    imageFile,
    profileItems,
}: ReturnType<typeof UpdateUserProfileImage>) {
    const user: User = yield UserApi.UpdateUserImage(imageFile, profileItems);
    yield put(UpdateUserProfileImageComplete(user));
}
// 新規スキル作成
function* createNewSkillWorker({ newSkill }: ReturnType<typeof CreateNewSkill>) {
    let result: any = yield UserApi.createNewSkill(newSkill);
    let error = "";
    if (result.status && result.status === 500) {
        error = result.message;
        result = yield UserApi.GetSkillList([]);
        yield put(CreateNewSkillFailed(result, error));
    } else {
        yield put(CreateNewSkillCompleted(result));
    }
}

// スキル取得・検索
function* GetSkillListWorker({ inputSkillList }: ReturnType<typeof GetSkillList>) {
    const outputSkillList: UserSkill[] = yield UserApi.GetSkillList(inputSkillList);
    yield put(GetSkillListComplete(outputSkillList));
}

// スキル取得・検索
function* StartSyncBatchWorker() {
    const response: any = yield UserApi.StartSyncBatch();
    if (response.response && response.response.status !== 200) {
        yield put(StartSyncBatchFailed(response.stack));
    } else {
        yield put(StartSyncBatchComplete());
    }
}

// グループリスト取得
function* GetGroupListWorker() {
    const response: any = yield UserApi.GetGroupList();
    yield put(GetGroupListComplete(response));
}
// グループリスト更新
function* UpdateGroupListWorker({ updateList }: ReturnType<typeof UpdateGroupList>) {
    const response: any = yield UserApi.UpdateGroupList(updateList);
    yield put(UpdateGroupListComplete(response));
}

// アクセス可能なメールドメインのリストを取得
function* GetAccessableEmailDomainListWorker() {
    const domainList: AccessableEmailDomain[] = yield UserApi.getAccessableEmailDomainList();
    yield put(GetAccessableEmailDomainListComplete(domainList));
}

// アクセス可能なメールドメインの更新
function* UpdateAccessableEmailDomainListWorker({
    domainList,
    userId,
}: ReturnType<typeof UpdateAccessableEmailDomainList>) {
    const newList: AccessableEmailDomain[] = yield UserApi.updateAccessableEmailDomainList(
        domainList,
        userId
    );
    yield put(UpdateAccessableEmailDomainListComplete(newList));
}

// アクセス可能なEMailリストを取得
function* GetAccessableEmailListWorker() {
    const emailList: AccessableEmail[] = yield UserApi.getAccessableEmailList();
    yield put(GetAccessableEmailListComplete(emailList));
}

// アクセス可能なEMAILの更新
function* UpdateAccessableEmailListWorker({
    emailList,
    userId,
}: ReturnType<typeof UpdateAccessableEmailList>) {
    const newList: AccessableEmail[] = yield UserApi.updateAccessableEmailList(emailList, userId);
    yield put(UpdateAccessableEmailListComplete(newList));
}

// ユーザーライセンス情報を取得
function* GetUserLicenseWorker({ subdomain }: ReturnType<typeof GetUserLicenseStart>) {
    const userLicense: UserLicense[] = yield UserApi.GetUserLicense(subdomain);
    yield put(GetUserLicenseComplete(userLicense));
}

export default function* UserSaga() {
    yield takeEvery(UserEvents.GetUserInfoListStarted, GetUserInfoList);
    yield takeEvery(UserEvents.UpdateUserInfoStarted, UpdateUserInfoWorker);
    yield takeEvery(UserEvents.LoginStarted, Login);
    yield takeEvery(UserEvents.UpdateUserProfileImageStarted, UpdateProfileImage);
    yield takeEvery(UserEvents.CreateNewSkillStarted, createNewSkillWorker);
    yield takeEvery(UserEvents.GetSkillListStarted, GetSkillListWorker);
    yield takeEvery(UserEvents.StartSyncBatch, StartSyncBatchWorker);
    yield takeEvery(UserEvents.GetGroupListStart, GetGroupListWorker);
    yield takeEvery(UserEvents.UpdateGroupListStart, UpdateGroupListWorker);
    yield takeEvery(
        UserEvents.GetAccessableEmailDomainListStart,
        GetAccessableEmailDomainListWorker
    );
    yield takeEvery(
        UserEvents.UpdateAccessableEmailDomainListStart,
        UpdateAccessableEmailDomainListWorker
    );
    yield takeEvery(UserEvents.GetAccessableEmailListStart, GetAccessableEmailListWorker);
    yield takeEvery(UserEvents.UpdateAccessableEmailListStart, UpdateAccessableEmailListWorker);
    yield takeEvery(UserEvents.GetUserLicenseStart, GetUserLicenseWorker);
}
